import { NavLink } from 'react-router-dom';
import { Activity, CreditCard, Gauge, LayoutDashboard, ShieldAlert, Sparkles, Users } from 'lucide-react';
import type { ComponentType } from 'react';
import { complianceEnabled } from '@config/env';
import { ComplianceTabBadge } from '@features/compliance/components/ComplianceTabBadge';
import { routes } from '@config/routes';
import { useCapabilities } from '@core/api/useCapabilities';
import { AdminProfile } from './AdminProfile';

interface Tab {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

/**
 * The single top header: brand mark + "Admin" wordmark on the left, the Dashboard / Users / AI-Costs nav
 * tabs in the centre-left, and the admin profile (email + sign-out + theme toggle) on the right. The
 * AI-Costs tab appears only when the backend reports the `aiUsage` capability (the `v2_ai_prompt_usage`
 * table exists) — so an older journal DB simply doesn't show it.
 *
 * The Compliance tab needs two things: the build-time `complianceEnabled` (dev builds only; a prod/demo
 * bundle folds it away) and a backend that does not report `compliance: false`. Like every tab it is
 * admin only because the whole app is: `AuthGuard` admits nobody outside the `admin` group.
 */
export function Header() {
  const caps = useCapabilities();
  const tabs: Tab[] = [
    { to: routes.dashboard, label: 'Dashboard', icon: LayoutDashboard },
    { to: routes.users,     label: 'Users',     icon: Users },
    ...(caps.subscriptions ? [{ to: routes.plans, label: 'Plans', icon: CreditCard }] : []),
    ...(caps.aiUsage ? [{ to: routes.ai, label: 'AI Costs', icon: Sparkles }] : []),
    ...(caps.infra ? [{ to: routes.infra, label: 'Infra', icon: Gauge }] : []),
    ...(complianceEnabled && caps.compliance !== false ? [{ to: routes.compliance, label: 'Compliance', icon: ShieldAlert }] : []),
  ];
  return (
    <header
      className="flex h-14 shrink-0 items-center gap-6 bg-canvas-raised px-4 sm:px-6"
      style={{ borderBottom: '1px solid var(--canvas-divider)' }}
    >
      {/* Brand */}
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'var(--text)' }}>
          <Activity className="h-4 w-4" style={{ color: 'var(--canvas-raised)' }} />
        </span>
        <div className="leading-tight">
          <div className="font-display text-[15px] font-bold tracking-tight text-text">Optimus Journal</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-text-subtle">Admin Console</div>
        </div>
      </div>

      {/* Nav tabs */}
      <nav className="flex items-center gap-1">
        {tabs.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${
                isActive ? '' : 'text-text-muted hover:bg-canvas-inset hover:text-text'
              }`
            }
            style={({ isActive }) =>
              isActive ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : undefined
            }
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
            {to === routes.compliance && complianceEnabled && <ComplianceTabBadge />}
          </NavLink>
        ))}
      </nav>

      <div className="ml-auto flex items-center gap-2">
        <AdminProfile />
      </div>
    </header>
  );
}
